import {TableCell, Collapse, TableRow, Button, CircularProgress, Grid, ImageListItem, ImageList, MenuItem, Menu, Dialog, IconButton} from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import React, { useState, useEffect, Fragment } from 'react';
import {useTranslation} from "react-i18next";
import {useNavigate} from 'react-router';
import {observer} from 'mobx-react';
import {dStr, tStr, TAGT_ALL_TIME_FIRST, TAGT_YEAR_FIRST, TAGT_USER_FIRST, TAGT_USER_YEAR_FIRST, c2js, unixNow, delOfflineObs, LightenColor} from './Util.js';
import {sexList, accuracyList, ageList, plumageList, uncertLevelList, brCodeList} from './Lists.js';
import {MTI_JPG, MTI_PNG, MTI_MP3, MTI_WAV, MTI_XWAV} from './taxons/TaxMedia.js';
import {getSpecName, getCityName, getCountryName} from './Trans.js';
import {useGlobalStorage} from './mobx/GlobalStorage.js';
import UploadButton from './UploadButton.js';
import {COMM_OK} from './WsR2.js';
import {obsNum} from './Main.js';

let timeout = null;

function longPressEvents(callback, ms = 700) {
	let posRef = {x: 0, y: 0};

	const handleEv = ((ev) => {
// 		console.log('handleEv');
		callback(posRef);
	})
	
	const start = ((ev) => {
		timeout = setTimeout(handleEv, ms);
// 		console.log('start', timeout, ev.clientX);
		posRef = {x: ev.clientX, y: ev.clientY};
	})

	const stop = ((ev) => {
// 		console.log('stop', timeout);
		timeout && window.clearTimeout(timeout);
	})

	return callback
	? {
		onMouseDown: start,
		onMouseUp: stop,
		onMouseLeave: stop,
		onTouchStart: start,
		onTouchMove: stop,
		onTouchEnd: stop
	}
	: {};
}

function getImageSrc(fi, full = false) {
	let mt = '';

	if (fi.mt_id === MTI_JPG) {
		mt = 'image/jpeg';
	} else if (fi.mt_id === MTI_PNG) {
		mt = 'image/png';
	} else {
		console.error('getImageSrc: invalid media type', fi.mt_id, '!');
		return null;
	}

	if (full && fi.full_src)
		return 'data:' + mt + ';base64,' + fi.full_src;

	return 'data:' + mt + ';base64,' + fi.src;
}

function getBrCode(code) {
	if (code % 10 === 0) 
		return code / 10;

	return code;
}

const HorizImageList = ((props) => {
	if (!props.files || props.files.length === 0)
		return null;

	const ncol = Math.round(Math.max(window.innerWidth / 96, props.files.length));

// 	console.log('HorizImageList', props.files.length, props.files);

	return (
		<ImageList cols={ncol} style={{marginLeft: 16, margin: 8}}>
			{props.files.map((item, pos) => {
			    if (item.mt_id !== MTI_JPG && item.mt_id !== MTI_PNG)
				    return null;

			    return (
				<ImageListItem 
				    key={item.id} 
				    onClick={props.onClick.bind(this, item, pos)} 
				    onContextMenu={props.contextClick.bind(this, item, pos)} 
				    {...longPressEvents(props.longPress.bind(this, item, pos))} 
				    style={{ height: 96, width: 96 }}>
					{item.src ? 
					    <img
						src={getImageSrc(item)}
						srcSet=""
						alt={item.name}
						style={{height: 96, width: 96, objectFit: 'scale-down'}}
						loading="lazy"/> :
					    <Grid container direction="row" justifyContent="center" alignItems="center" spacing={0}>
						<CircularProgress/>
					    </Grid>
					}
					{/*<ImageListItemBar
						title={item.caption}
						position="below"
					/>*/}
				</ImageListItem>
			    );
			})}
		</ImageList>
	);
});

const TaxonButton = ((props) => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');
	const navigate = useNavigate();
	const {obs} = props;

	function toTaxon(obs) {
		gStorage.taxons.setPendTaxon(gStorage.lists.speclist.list.find(x => x.id === obs.spec_id));
		navigate('/taxons');
	}

	return (
		<Button
			style={{margin: 1}}
			variant="text"
			onClick={() => toTaxon(obs)}>
			{t('main.to_taxon')}
		</Button>
	)
});

const RemoveMeButton = ((props) => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');
	const {obs} = props;

	function removeMe(obs) {
		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'obs',
			op: 'rm_user',
			data: {
				obs_id: obs.id,
				user_id: gStorage.conn.user.id
			}
		};

// 		console.log('Remove me', msg);
		gStorage.conn.ws.send(msg, props.rxWs);
	}

	// Itse luodut havainnot voi poistaa DEL-napilla.
	if (obs.user_id === gStorage.conn.user.id)
		return null;

	// Poistamista varten pitää olla havaitsijoiden listalla
	if (!obs.observers.includes(gStorage.conn.user.id))
		return null;

	return (
		<Button
			style={{margin: 1}}
			variant="text"
			onClick={() => removeMe(obs)}>
			{t('main.remove_me')}
		</Button>
	)
});

const RemoveObsButton = ((props) => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');

	function dataValidForDel() {
		if (props.obs.user_id !== gStorage.conn.user.id && gStorage.conn.user.id !== 1)
			return 0;

		return props.obs.id !== 0;
	}

	function delObs() {
		if (props.obs.id < 0) {
			let ret = delOfflineObs(props.obs.id);
			if (ret < 0) {
				alert('delOfflineObs failed!');
				return;
			}

			props.rxWs(COMM_OK, {cmd: 'obs', op: 'del', result: 'OK'});
		} else {
			if (!gStorage.conn.online) {
				alert('Attempting to delete online obs when offline!');
				return;
			}

			let msg = {
				sess_id: gStorage.conn.sess_id,
				cmd: 'obs',
				op: 'del',
				data: {
					id: props.obs.id
				}
			}

//			console.log(JSON.stringify(msg));
			gStorage.conn.ws.send(msg, props.rxWs);
		}
	}

	return (
		<Button 
		    style={{margin: 1}} 
		    variant="text" 
		    onClick={() => {
			if (window.confirm(t('add.del_conf'))) 
				delObs()
				
		    }} 
		    disabled={!dataValidForDel()}>
			{t('add.del')}
		</Button>
	);
});

const ObsRow = observer((props) => {
	const [audioPlaying, setAudioPlaying] = useState(-1);
	const [bigOpen, setBigOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(0);
	const [menuPos, setMenuPos] = useState(null);
	const [ownObs, setOwnObs] = useState(null);
	const [isOpen, setOpen] = useState(false);
	const gStorage = useGlobalStorage();
	let d_str, t_str, a_str, bg, time;
	const {t} = useTranslation('obs');
	const navigate = useNavigate();

	useEffect(() => {
// 		console.log('useEffect');
		if (!ownObs || !gStorage.conn.online)
			return;
		
		if (!ownObs.subobs) {
// 			console.log('useEffect: load subobs', ownObs);
			loadSingleOp();
		} else if (ownObs.thumb_pos < ownObs.files.length) {
// 			console.log('useEffect: load thumb', ownObs.thumb_pos, ownObs.files.length);
			loadThumb();
		} else if (ownObs.file_pos >= 0) {
// 			console.log('useEffect: load media', ownObs.file_pos);
			loadMedia();
		}
		// eslint-disable-next-line
	}, [ownObs, gStorage.conn.online]);

	function mainGetSpecName(id) {
		return getSpecName(gStorage.lists.speclist.list.find(option => option.id === id), gStorage.conn.user);
	}

	function editObs(obs) {
		gStorage.add.setFromVoice(false);
		gStorage.main.setEditObs(obs);
		navigate('/add');
	}

// 	function showOnMap (lat, long) {
// 		if (lat !== 0 && long !== 0) {
// 			window.open('https://www.google.com/maps/place/' + lat / 1000000.0 + ',' + long / 1000000.0, '_blank', 'noopener,noreferrer');
// 		}
// 	}

	function userArraySortFn(a, b) {
		let u1, u2;

		if (a === props.obs.user_id)
			return -1;
		if (b === props.obs.user_id)
			return 1;

		if (a === gStorage.conn.user.id)
			return -1;
		if (b === gStorage.conn.user.id)
			return 1;

		u1 = gStorage.lists.userlist.list.find((usr) => usr.id === a);
		u2 = gStorage.lists.userlist.list.find((usr) => usr.id === b);

		if (u1.last_name !== u2.last_name)
			return u1.last_name.localeCompare(u2.last_name, 'fi');

		return u1.first_name.localeCompare(u2.first_name, 'fi');
	}

	function userArray(ul) {
		let str, usr;

		str = '';

		ul.sort(userArraySortFn);

//		console.log('userArray', props.obs.user_id, ul);

		ul.forEach((uid, i) => {
			usr = gStorage.lists.userlist.list.find((usr) => usr.id === uid);
			if (usr && (uid === gStorage.conn.user.id || usr.allow_friend === 1)) {
				if (str.length) {
					if (i === 1) {
						str += ' (';
					} else {
						str += ', ';
					}
				}

				if (usr.first_name.length)
					str += usr.first_name;
				
				if (usr.last_name.length) {
					if (str.length && usr.last_name.length)
						str += ' ';

					str += usr.last_name;
				}

				if (i >= 1 && i >= ul.length - 1)
					str += ')';
			}
		});

		return str;
	}

	function tripArray(trips) {
		let str = '';

		if (!gStorage.lists.triplist)
			return;

		trips.forEach((tid, i) => {
			const trip = gStorage.lists.triplist.list.find((trip) => trip.id === tid);
			if (trip) {
				if (i > 0)
					str += ', ';

				str += trip.name;
			}
		});

		return str;
	}

	function obsDet(obs) {
		/* Offline obs? */
		if (props.obs.id > 0) {
			return obs.sum_desc.split(',');
		} else {
			return props.obs.subobs.map((so) => {
				return so.num + ' ' + so.sum_desc;
			})
		}
	}

	function getFormattedAddr(obs) {
		const city = gStorage.lists.citylist.list.find(x => x.id === obs.city_id);
		const usr = gStorage.conn.user;

		let a_str = '';

		if (city.country !== usr.def_country) {
			const country = gStorage.lists.countrylist.find(x => x.id === city.country);
			a_str = getCountryName(country, usr) + ', ';
		}

		a_str += getCityName(city, usr) + ', ' + obs.addr;

		return a_str;
	}

	/* ------------------------------------------------------------------ */

	time = props.obs.id < 0 ? props.obs.start_utc : props.obs.start_t;
	d_str = dStr(time);
	if (d_str === dStr(unixNow()))
		d_str = '';

	t_str = tStr(time);
	if (t_str.length > 0 && t_str !== '0:00')
		d_str += ' ' + t_str;

	/* ------------------------------------------------------------------ */

	a_str = getFormattedAddr(props.obs);

	/* ------------------------------------------------------------------ */

	if (props.obs.id <= 0) {
		/* offline obs */
		bg = '#ffc107';
	} else if (props.obs.user_id === gStorage.conn.user.id && props.obs.color1 && gStorage.conn.user.color1 && gStorage.conn.user.color1_desc.length > 0) {
		bg = c2js(gStorage.conn.user.color1);
	} else if (props.obs.user_id === gStorage.conn.user.id && props.obs.color2 && gStorage.conn.user.color2 && gStorage.conn.user.color2_desc.length > 0) {
		bg = c2js(gStorage.conn.user.color2);
	} else {
		bg = props.tgl ? '#f0f0f0' : '#ffffff';

		if (props.obs.tags) {
			let res;

			res = props.obs.tags.findIndex((t) => t.t_id === TAGT_ALL_TIME_FIRST);
			if (res >= 0) {
// 				console.log('All time first', mainGetSpecName(props.obs.spec_id));
				bg = '#7bd147';
			} else {
				res = props.obs.tags.findIndex((t) => t.t_id === TAGT_YEAR_FIRST);
				if (res >= 0) {
// 					console.log('Year first', mainGetSpecName(props.obs.spec_id));
					bg = '#cbffe2';
				} else {
					res = props.obs.tags.findIndex((t) => t.t_id === TAGT_USER_FIRST && t.add_int_2 === gStorage.conn.user.id);
					if (res >= 0) {
// 						console.log('User first', mainGetSpecName(props.obs.spec_id));
						bg = '#00c7ce';
					} else {
						res = props.obs.tags.findIndex((t) => t.t_id === TAGT_USER_YEAR_FIRST && t.add_int_2 === gStorage.conn.user.id);
						if (res >= 0) {
// 							console.log('User year first', mainGetSpecName(props.obs.spec_id));
							bg = '#bdfffe';
						}
					}
				}
			}
		}
	}

	function loadThumb() {
// 		console.log('loadThumb', ownObs.thumb_pos, ownObs.files[ownObs.thumb_pos].mt_id);
		
		if (ownObs.files[ownObs.thumb_pos].mt_id === MTI_JPG || ownObs.files[ownObs.thumb_pos].mt_id === MTI_PNG) {
			let msg = {
				sess_id: gStorage.conn.sess_id,
				cmd: 'file',
				op: 'thumbnail',
				data: {
					file: {
						id: ownObs.files[ownObs.thumb_pos].id,
					}
				}
			};

			gStorage.conn.ws.send(msg, singleObsRxWs);
		} else {
			let obs = JSON.parse(JSON.stringify(ownObs));

			obs.thumb_pos++;
			setOwnObs(obs);
		}
	}

	function loadMedia() {
		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'file',
			op: 'download',
			data: {
				file: {
					id: ownObs.files[ownObs.file_pos].id,
				}
			}
		};

		if (ownObs.files[ownObs.file_pos].full_src)
			return;

// 		console.log('loadMedia', ownObs.file_pos, ownObs.files[ownObs.file_pos].id, ownObs.files[ownObs.file_pos].name);

		gStorage.conn.ws.send(msg, singleObsRxWs);
	}
	
	function singleObsRxWs(status, msg) {
		if (status !== COMM_OK || msg.result !== 'OK') {
			console.error('singleObsRxWs: error ', status);

			clearTimeout(isLoading);
			setIsLoading(0);
			setOpen(true);

			return -1;
		}

// 		console.log('singleObsRxWs', msg);

		if (msg.cmd === 'obs') {
			if (msg.op === 'load') {
				let obs = JSON.parse(JSON.stringify(ownObs));

				clearTimeout(isLoading);
				setIsLoading(0);

				obs.subobs = msg.data.obs.subobs;
				obs.trips = msg.data.obs.trips;
				obs.files = msg.data.obs.files;

				obs.thumb_pos = 0;
// console.log('singleObsRxWs', obs);
				setOwnObs(obs);
				setAudioPlaying(-1);
			} else {
				console.error('singleObsRxWs: unknown operation:', msg.cmd, msg.op);
			}
		} else if (msg.cmd === 'file') {
			if (msg.op === 'thumbnail') {
				let obs = JSON.parse(JSON.stringify(ownObs));

				console.log('file thumbnail rx', obs.thumb_pos);

				obs.files[obs.thumb_pos].src = msg.data.file.content;
				obs.thumb_pos++;

				setOwnObs(obs);
			} else if (msg.op === 'detach') {
				let obs = JSON.parse(JSON.stringify(props.obs));

				obs.files = [];
				obs.thumb_pos = 0;

				setOwnObs(obs);
				setAudioPlaying(-1);
			} else if (msg.op === 'download') {
				let obs = JSON.parse(JSON.stringify(ownObs));


				const fi = obs.files[obs.file_pos];

				if ([MTI_MP3, MTI_WAV, MTI_XWAV].includes(fi.mt_id)) {
					const tgt = document.getElementById('audio_' + obs.file_pos);

					tgt.src = getAudioSrc(fi, msg.data.file.content);
					tgt.play();
					setAudioPlaying(obs.file_pos);

					obs.files[obs.file_pos].full_src = 1;
				} else {
					obs.files[obs.file_pos].full_src = msg.data.file.content;
				}

				setOwnObs(obs);
			}
		} else {
			console.error('singleObsRxWs: unknown cmd', msg.cmd, msg.op);
		}
	}

	function loadSingleOp() {
		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'obs',
			op: 'load',
			data: {
				id: ownObs.id,
			},
		}

		gStorage.conn.ws.send(msg, singleObsRxWs);

		setIsLoading(setTimeout(() => {
			setIsLoading(0);
		}, 2000));
	}

	function uploadDone() {
		let obs = JSON.parse(JSON.stringify(ownObs));

// 		console.log('uploadDone');

		delete obs.subobs;
		delete obs.trips;

		obs.files = [];
		obs.thumb_pos = 0;

		setOwnObs(obs);
		setAudioPlaying(-1);
	}

	function tglOpen() {
		if (!isOpen) {
// 			console.log('tglOpen', props.obs.subobs);

			if (gStorage.conn.online && (!ownObs || !ownObs.subobs)) {
				let obs = JSON.parse(JSON.stringify(props.obs));

				obs.files = [];
				obs.thumb_pos = 0;

				setOwnObs(obs);
			}
		}

		setOpen(!isOpen);
	}

	function soDesc(so) {
		let str = so.num;

		if (so.acc_id > 1) {
			str += ' (';
			str += accuracyList.find((a) => a.id === so.acc_id).acc;
			str += ')';
		}

		if (so.sex_id > 1) {
			str += ' ';
			str += sexList.find((s) => s.id === so.sex_id).sex;
		}

		if (so.age_id > 1) {
			str += ' ';
			str += ageList.find((a) => a.id === so.age_id).age;
		}

		if (so.plumage_id > 1) {
			str += ' ';
			str += plumageList.find((p) => p.id === so.plumage_id).plumage;
		}
	
		str += ' ' + so.sum_desc;

		if (so.memo)
			str += ' ' + so.memo;

		return str;
	}

	function obsFileRightClick(img, pos, ev) {
		console.log('obsFileRightClick', img, pos, ev);
		ev.preventDefault();
		setMenuPos(menuPos === null ? {mouseX: ev.clientX - 2, mouseY: ev.clientY - 4, img: img, imgPos: pos} : null);
	}

	function handleImageDelete() {
		console.log('handleImageDelete', menuPos.img.id, menuPos.img.name);

		if (!window.confirm(t('add.media_del_conf'))) 
			return;

		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'file',
			op: 'detach',
			data: {
				file: {
					id: menuPos.img.id,
				},
				target: {
					table: 'obs',
					id: ownObs.id
				}
			}
		};

		gStorage.conn.ws.send(msg, singleObsRxWs);
		setMenuPos(null);
	}

	function handleMenuClose() {
		setMenuPos(null);
	};

	function openMediaProps() {
		console.log('openMediaProps', menuPos.img.id, menuPos.img.name);
	}

	/* Right click when menu is open: close menu! */
	function onMenuContextClick(ev) {
		ev.preventDefault();
		setMenuPos(null);
	}
	
	function obsLongPress(img, pos, ev) {
		console.log('obsLongPress', img, pos, ev);
		setMenuPos(menuPos === null ? {mouseX: ev.x - 2, mouseY: ev.y - 4, img: img, imgPos: pos} : null)
	}

	/* Left click on image. */
	function obsFileClick(img, pos, ev) {
		let obs = JSON.parse(JSON.stringify(ownObs));

		console.log('obsFileClick', img, pos, ev);

		obs.file_pos = pos;
		setOwnObs(obs);
		setBigOpen(true);
	}

	function handleBigClose() {
		console.log('handleBigClose');
		setBigOpen(false);
	}
	
	function getAudioSrc(fi, src) {
		let mt = '';

// 		console.log('getAudioSrc', fi);
		
		if (!src)
			return null;

		if (fi.mt_id === MTI_WAV) {
			mt = 'audio/wav';
		} else if (fi.mt_id === MTI_MP3) {
			mt = 'audio/mp3';
		} else if (fi.mt_id === MTI_XWAV) {
			mt = 'audio/x-wav';
		} else {
			console.error('getAudioSrc: invalid media type', fi.mt_id, '!');
			return null;
		}

		return 'data:' + mt + ';base64,' + src;
	}

	/* Left click on sound file. */
	function soundClick(snd, pos, ev) {
		const tgt = document.getElementById('audio_' + pos);
// 		console.log('soundClick', snd, pos);

		if (audioPlaying >= 0) {
			if (audioPlaying === pos) {
				tgt.pause();
				setAudioPlaying(-1);
				return;
			} else {
				const curr = document.getElementById('audio_' + audioPlaying);
				curr.pause();
				setAudioPlaying(-1);
			}
		}

		if (snd.full_src) {
			tgt.play();
			setAudioPlaying(pos);
		} else {
			let obs = JSON.parse(JSON.stringify(ownObs));
			obs.file_pos = pos;
			setOwnObs(obs);
		}
	}

	function soundEnded(snd, pos, ev) {
		console.log('soundEnded', snd.id, snd.name, pos);
		setAudioPlaying(-1);
	}

	function soundDelete(snd, pos, ev) {
		if (!window.confirm(t('add.media_del_conf'))) 
			return;

		console.log('soundDelete', snd, pos, ev);

		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'file',
			op: 'detach',
			data: {
				file: {
					id: snd.id,
				},
				target: {
					table: 'obs',
					id: ownObs.id
				}
			}
		};

		gStorage.conn.ws.send(msg, singleObsRxWs);
	}

	function uncertaintyDesc(level) {
		const uc = uncertLevelList.find((uc) => uc.id === level);
		let str = t('add.uncertainty') + ': ';

		return str + (uc.desc.length ? t(uc.name) + ', ' + t(uc.desc) : t(uc.name));
	}
	
// 	console.log('ObsRow', props.obs.files, props.obs);

	return (
		<Fragment>
			<TableRow key={props.obs.id} style={{backgroundColor: bg, '& > *': { borderBottom: 'unset' }}} onClick={tglOpen}>
				<Fragment>
					<TableCell sx={{cursor: 'pointer', minWidth: 150,padding: 2, margin: 1}}>{mainGetSpecName(props.obs.spec_id)}</TableCell>
					<TableCell sx={{cursor: 'pointer', minWidth: 80, padding: 2, margin: 1}}>{d_str}</TableCell>
					<TableCell sx={{cursor: 'pointer', minWidth: 180, padding: 2, margin: 1}}>{a_str}</TableCell>
					<TableCell sx={{cursor: 'pointer', minWidth: 120, padding: 2, margin: 1}} align='right'>{obsNum(props.obs)}</TableCell>
				</Fragment>
			</TableRow>
			<TableRow sx={{backgroundColor: LightenColor(bg, 7)}}>
				{isLoading ? 
					<TableCell colSpan={5}>
						<Grid container direction="row" justifyContent="center" alignItems="center" spacing={0}>
							<Collapse in={isOpen} timeout="auto" unmountOnExit>
								<CircularProgress/>
							</Collapse>
						</Grid>
					</TableCell>
					:
					<Fragment>
						<TableCell sx={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
							<Collapse in={isOpen} timeout="auto" unmountOnExit>
								{
								    ownObs && ownObs.subobs ? 
									ownObs.subobs.map((so, i) => {
										return (<div key={i} style={{marginLeft: 16, margin: 8}}>{soDesc(so)}</div>);
									}) :
									obsDet(props.obs).map((s, i) => {
										return (<div key={i} style={{marginLeft: 16, margin: 8}}>{s}</div>);
									})
								}
								{
									ownObs && ownObs.memo ? 
										<div style={{marginLeft: 16, margin: 8}}>
											{ownObs.memo}
										</div>
									: null
								}

								<div style={{marginLeft: 16, margin: 8}}>
									{userArray(props.obs.observers)}
								</div>

								{
									ownObs && ownObs.uncertainty ? 
										<div style={{marginLeft: 16, margin: 8, color: 'orange'}}>
											{uncertaintyDesc(ownObs.uncertainty)}
										</div>
									: null
								}

								{
									ownObs && ownObs.fin_br_code ? 
										<Fragment>
											<div style={{marginLeft: 16, margin: 8}}>{getBrCode(ownObs.fin_br_code) + ' ' + t(brCodeList.find(brc => brc.id === ownObs.fin_br_code).cat)}</div>
											<div style={{marginLeft: 32, margin: 8}}>{t(brCodeList.find(brc => brc.id === ownObs.fin_br_code).desc)}</div>
										</Fragment>
									: null
								}

								{
								    ownObs && ownObs.trips ? 
									<div style={{marginLeft: 16, margin: 8}}>
										{tripArray(ownObs.trips)}
									</div>
									:
									null
								}

								<HorizImageList 
								    files={ownObs && ownObs.files}
								    onClick={obsFileClick} 
								    contextClick={obsFileRightClick} 
								    longPress={obsLongPress}/>

								{
								    ownObs && ownObs.files ? 
									ownObs.files.map((item, pos) => {
										// console.log('map', pos, item.mt_id, MTI_WAV, MTI_MP3);

										if (item.mt_id !== MTI_WAV && item.mt_id !== MTI_MP3 && item.mt_id !== MTI_XWAV)
											return null;

										return (
											<Fragment key={pos}>
												<div style={{margin: 4}} >
													<IconButton size="small" color="inherit" onClick={soundClick.bind(this, item, pos)}>
														{audioPlaying === pos ? 
															<PauseCircleOutlineIcon />
															:
															<PlayCircleOutlineIcon />
														}
													</IconButton>
													<audio preload='none' id={'audio_' + pos} onEnded={soundEnded.bind(this, item, pos)}/>
													{item.name}
													{props.obs.user_id === gStorage.conn.user.id ?
														<IconButton size="small" color="inherit" onClick={soundDelete.bind(this, item, pos)}>
															<DeleteOutlineIcon/>
														</IconButton>
													: null}
												</div>
											</Fragment>);
									})
									: 
									null
								}

								<Button
								    style={{margin: 1}}
								    variant='text'
								    disabled={!gStorage.conn.online && props.obs.id > 0}
								    onClick={() => editObs(props.obs)}>
									{props.obs.user_id === gStorage.conn.user.id ? t('main.edit') : t('main.observe')}
								</Button>

								<TaxonButton
								    obs={props.obs}/>

								<RemoveMeButton 
								    obs={props.obs} 
								    rxWs={props.rxWs}/>

								<RemoveObsButton
								    obs={props.obs} 
								    rxWs={props.rxWs}/>
								
								{/*<Button
								    style={{margin: 1}}
								    variant='text'
								    onClick={() => showOnMap(props.obs.lat_obs, props.obs.long_obs)}>
									{t('main.maps')}
								</Button>*/
								}

								<UploadButton 
									variant='text'
									disabled={!gStorage.conn.online || props.obs.id <= 0}
									refresh={uploadDone} 
									target={{table: 'obs', id: props.obs.id}}
									filemask={'image/png,image/jpeg,audio/wav,audio/mpeg'}
									caption = 'common.add_media'/> 
							</Collapse>
						</TableCell>

						<Menu
						    onContextMenu={onMenuContextClick}
						    open={menuPos !== null}
						    onClose={handleMenuClose}
						    anchorReference="anchorPosition"
						    anchorPosition={menuPos !== null ? { top: menuPos.mouseY, left: menuPos.mouseX } : undefined}>
							{
							    props.obs.user_id === gStorage.conn.user.id ? 
								<MenuItem onClick={handleImageDelete}>
									{t('media.delete')}
								</MenuItem>
								: 
								null
							}
							<MenuItem onClick={openMediaProps}>
								{t('media.properties')}
							</MenuItem>
						</Menu>

						<Dialog
						    open={bigOpen}
						    onClose={handleBigClose}
						    style={{ maxWidth: "100%", maxHeight: "100%" }}>
							<img
							    alt=""
							    style={{ width: 'auto', height: '100%' }}
							    src={ownObs && ownObs.files && 
								    ownObs.file_pos >= 0 && ownObs.file_pos < ownObs.files.length &&
								    (ownObs.files[ownObs.file_pos].mt_id === MTI_JPG || ownObs.files[ownObs.file_pos].mt_id === MTI_PNG) ? 
									getImageSrc(ownObs.files[ownObs.file_pos], true) :
								    null}
							    onClick={handleBigClose}/>
						</Dialog>
					</Fragment>
				}
			</TableRow>
		</Fragment>
	);
});

export default ObsRow;
